<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import router from '@/router'
import { menuRoutes } from '@/router'
import { usePortalStore } from '@/stores/portal.store'

import DocumentRelationDialog from './DocumentRelationDialog.vue'

const i18n = useI18n()
const portalStore = usePortalStore()

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  textFields: {
    type: Array,
    default: () => []
  },
  text: {
    type: String,
    default: ''
  },
  queryFields: {
    type: Array,
    default: () => []
  },
  target: {
    type: String,
    default: null
  },
  isLink: {
    type: Boolean,
    default: true
  },
  clickAction: {
    type: Function,
    default: null
  },
  visible: {
    type: Boolean,
    default: false
  },
  color: {
    type: String,
    default: null
  },
  hasUnderline: {
    type: Boolean,
    default: false
  },
  fetchDocumentRelations: {
    type: Function,
    default: null,
  },
})

const relationDialogVisible = ref(false)

const getName = (path) => {
  return Object.entries(menuRoutes).find(([_, value]) => value === path)?.[0]
}

const getText = () => {
  return props.text ? props.text : props.textFields
    .map(field => props.data[field])
    .filter(val => val != null)
    .join(' ')
}

const openLink = async(forceTargetBlank = false) => {
  if (props.clickAction) {
    props.clickAction({
      targetBlank: forceTargetBlank,
      target: props.target,
      ...props
    })
    return
  }

  const args = {}

  for (const field of props.queryFields) {
    if (props.data[field] != null) {
      args[field] = props.data[field]
    }
  }

  const route = { path: props.target, query: args }
  if (forceTargetBlank) {
    const routeData = router.resolve(route)
    window.open(routeData.href, '_blank')
  } else {
    await router.push(route) // delay to allow rendering of other components
    portalStore.incrementReloadKey()
  }
}

</script>
<template>
  <div
    :v-if="getText().length > 0 || props.visible"
    class="menu-item"
    style="display: flex; align-items: center; justify-content: space-between;"
  >
    <div>
      <a
        v-if="isLink"
        class="link"
        :style="{ color: props.color ? props.color : '', 'text-decoration': props.hasUnderline ? 'underline' : 'none' }"
        @click="openLink(false)"
      > {{ getText() }} </a>
      <span v-else> {{ getText() }} </span>
      <slot />
      <q-icon
        name="mdi-open-in-new"
        :color="props.color || 'primary'"
        class="external-link"
        @click.stop="openLink(true)"
      >
        <q-tooltip class="bg-white text-black shadow-2">
          {{ i18n.t(getName(props.target)) }}
        </q-tooltip>
      </q-icon>
    </div>
    <div>
      <q-btn
        v-if="fetchDocumentRelations"
        icon="mdi-graph-outline"
        flat
        dense
        round
        size="xs"
        :title="i18n.t('common.documentRelation', 2)"
        @mousedown.stop
        @click.stop="relationDialogVisible = true"
      />
    </div>
    <document-relation-dialog
      v-if="relationDialogVisible"
      v-model:visible="relationDialogVisible"
      :fetch-document-relations="fetchDocumentRelations"
    />
  </div>
</template>

<style scoped lang="scss">

.link {
  cursor: pointer;
  font-weight: bold;
}

.external-link {
  margin-left: .25rem;
  cursor: pointer;
  opacity: 1;
}
</style>
